import {
  Checkbox,
  getFontCss,
  Popup,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Accordion from 'ext-ui/components/Accordion/Accordion'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import SingleDropdown from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import { spacing } from 'ext-ui/style/spacing'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { AnalyticsPageName } from 'src/analytics/analytics.models'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { SyncTransactionsSuccessToastMessage } from 'src/app/Toast/Toast'
import ConfirmationPopupContent from 'src/components/ConfirmationPopupContent/ConfirmationPopupContent'
import { OrganizationConnectedPlatform } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { ConnectedPlatformStatus } from 'src/models/transaction.models'
import { usePostPushTransactions } from 'src/queries/integrations/syncTransactionsPopup.queries'
import {
  ExpenseCategoryType,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import { useQueryFetchedTransactions } from 'src/queries/transactions.queries'
import { createSetIsSyncTransactionsPopupOpen } from 'src/redux/actions/app.actions'
import { updateSelectedTransactions } from 'src/redux/actions/data/transactions.actions'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import {
  selectIsSelectAllTransactionsFromState,
  selectSelectAllSyncableTransactionsFiltersFromState,
  selectSelectedTransactionIdsFromState,
} from 'src/redux/selectors/data/transactions.selectors'
import { css } from 'styled-components'

const SyncTransactionsPopup: React.FC = (): React.ReactElement => {
  const selectedTransactionIds = useSelector(
    selectSelectedTransactionIdsFromState,
  )
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const orgId = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )?.id
  const { data: expenseDetails } = useQueryExpenseCategories({ filters: {} })
  const {
    mutate: postPushTransactions,
    isPending: postPushingTransactions,
    error: postPushingTransactionsError,
  } = usePostPushTransactions()
  const dispatch = useBackgroundDispatch()
  const { data } = useQueryFetchedTransactions()
  const selectedTransactions = useMemo(
    () =>
      (data?.transactions ?? []).filter((txn) =>
        selectedTransactionIds.has(txn.id),
      ),
    [data, selectedTransactionIds],
  )
  const { data: mandatoryExpenseDetails } = useQueryExpenseCategories({
    filters: { active: true, requiredForTransactions: true },
  })
  const selectedSyncableTransactions = selectedTransactions.filter((txn) =>
    txn.canPushToConnectedPlatform(
      activeIntegration?.connectedPlatform,
      mandatoryExpenseDetails,
    ),
  )

  const translate = useTranslate()

  const { analytics } = useAnalyticsContext()
  useEffect((): void => {
    analytics?.page(AnalyticsPageName.SyncTransactionModal)
  }, [])

  const [selectedBankAccountId, setSelectedBankAccountId] = useState(
    activeIntegration?.bankAccount?.id,
  )

  const [syncAttachments, setSyncAttachments] = useState(false)

  const [selectedBankAccountError, setSelectedBankAccountError] = useState<
    string | undefined
  >(undefined)

  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false)

  const bankAccountOptions = activeIntegration?.bankAccountOptions?.map(
    (option) => {
      return {
        value: option.id,
        label: option.accountName,
      }
    },
  )

  const hasSyncedSuppliers = expenseDetails?.some(
    (expDetail) => expDetail.type === ExpenseCategoryType.Vendor,
  )
  const selectAllSyncableTransactionsFilters = useSelector(
    selectSelectAllSyncableTransactionsFiltersFromState,
  )
  const allSelected = useSelector(selectIsSelectAllTransactionsFromState)
  const hasMissingSuppliers =
    hasSyncedSuppliers &&
    selectedSyncableTransactions.some(
      (t) =>
        t.expenseDetails?.find(
          (expDetails) =>
            expDetails.categoryType === ExpenseCategoryType.Vendor,
        ) === undefined,
    )
  const showMissingSuppliersMessage = hasMissingSuppliers || allSelected
  const hasReceipts =
    selectedSyncableTransactions.filter((t) => t.hasAttachments).length > 0

  const dispatchSyncTransactions = (): void => {
    if (selectedBankAccountId === '' || selectedBankAccountId === undefined) {
      setSelectedBankAccountError(translate('Please select a bank account'))
      return
    }
    setSelectedBankAccountError(undefined)
    if (activeIntegration === undefined) return
    postPushTransactions(
      {
        organizationId: activeIntegration.organizationId,
        connectionId: activeIntegration.id,
        connectedPlatformName: activeIntegration.platformName ?? '',
        filters: selectAllSyncableTransactionsFilters,
        body: {
          transactionIds: allSelected
            ? undefined
            : selectedSyncableTransactions.map((txn) => txn.id),
          bankAccountId: selectedBankAccountId,
          pushAttachments: syncAttachments,
        },
      },
      {
        onSuccess: () => {
          dispatch(updateSelectedTransactions())
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <SyncTransactionsSuccessToastMessage
                  orgIntegrationDisplayName={activeIntegration.platformName}
                />
              ),
            }),
          )
          dispatch(createSetIsSyncTransactionsPopupOpen(false))
        },
      },
    )
    analytics?.track('sync transactions to ERP', {
      count:
        activeIntegration !== undefined
          ? selectedSyncableTransactions.filter((txn) => {
              return !txn.connectedPlatforms
                ?.filter(
                  (platform) =>
                    platform.syncStatus === ConnectedPlatformStatus.Success,
                )
                .map((platform) => platform.platform)
                .includes(activeIntegration.connectedPlatform)
            }).length
          : 0,
    })
  }

  const closePopup = (): void => {
    dispatch(createSetIsSyncTransactionsPopupOpen(false))
  }

  useEffect(() => {
    if (selectedBankAccountError || !selectedBankAccountId) {
      setIsAdvancedExpanded(true)
    }
  }, [selectedBankAccountError, selectedBankAccountId])

  return (
    <Popup
      onClickClose={closePopup}
      showCloseButton
      css={`
        display: flex;
        flex-direction: column;
        position: relative;
      `}
    >
      <ConfirmationPopupContent
        title={translate('Sync to ${orgLvlIntegration}', {
          orgLvlIntegration: activeIntegration?.platformName ?? '',
        })}
        subtitle={
          <>
            <div
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {translate(
                'Once the selected transactions have been synced to ${orgLvlIntegration}, ${bolded}.',
                {
                  orgLvlIntegration: activeIntegration?.platformName ?? '',
                  bolded: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                      `}
                    >
                      {translate('you cannot re-sync them with new data')}
                    </span>
                  ),
                },
              )}
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                margin-top: 20px;
                row-gap: 40px;
              `}
            >
              <div
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                  line-height: 18px;
                `}
              >
                {translate(
                  'Please confirm that you want to sync these transactions.',
                )}
              </div>

              {hasReceipts &&
                activeIntegration?.connectedPlatform !==
                  OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP && (
                  <Checkbox
                    isSelected={syncAttachments}
                    onClick={(): void => setSyncAttachments(!syncAttachments)}
                    label={
                      <span
                        css={css`
                          ${getFontCss(
                            14,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.regular,
                          )};
                          line-height: 18px;
                          letter-spacing: 0;
                        `}
                      >
                        {translate(
                          'Sync attachments associated with the transactions',
                        )}
                      </span>
                    }
                    testId={'syncAttachments'}
                  />
                )}
            </div>
            {showMissingSuppliersMessage && (
              <AlertPanel
                variant="warning"
                css={css`
                  margin-top: 20px;
                  padding: 12px 20px;
                `}
              >
                <span
                  css={css`
                    ${getFontCss(
                      12,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  {translate(
                    '${bolded} If you sync these transactions, the default value will show as “EXTEND” in ${orgLvlIntegration}.',
                    {
                      orgLvlIntegration: activeIntegration?.platformName ?? '',
                      bolded: (
                        <span
                          css={css`
                            ${getFontCss(
                              12,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.medium,
                            )};
                          `}
                        >
                          {translate(
                            'Some of your selected transactions are missing vendors.',
                          )}
                        </span>
                      ),
                    },
                  )}
                </span>
              </AlertPanel>
            )}
            {orgId !== undefined && bankAccountOptions !== undefined && (
              <div
                css={css`
                  margin-top: ${spacing(5)};
                `}
              >
                <Accordion
                  title={translate('Advanced')}
                  expanded={isAdvancedExpanded}
                  onExpandedChange={(expanded) => {
                    if (!expanded) {
                      setSelectedBankAccountId(
                        activeIntegration?.bankAccount?.id,
                      )
                    }
                    setIsAdvancedExpanded(expanded)
                  }}
                >
                  <SingleDropdown
                    data-testid={'selectBankAccount'}
                    label={translate(
                      'Select ${orgLvlIntegration} account to sync transactions to:',
                      {
                        orgLvlIntegration:
                          activeIntegration?.platformName ?? '',
                      },
                    )}
                    options={bankAccountOptions}
                    selectedValue={
                      selectedBankAccountId
                        ? {
                            value: selectedBankAccountId,
                            label: bankAccountOptions.find(
                              (opt) => opt.value === selectedBankAccountId,
                            )?.label,
                          }
                        : undefined
                    }
                    onSelect={({ value }): void => {
                      setSelectedBankAccountError(undefined)
                      setSelectedBankAccountId(value)
                    }}
                    errorText={selectedBankAccountError}
                    dropdownOptionsCount={4}
                  />
                </Accordion>
              </div>
            )}
          </>
        }
        actionButtonLabel={translate(
          'Confirm and Sync to ${orgLvlIntegration}',
          {
            orgLvlIntegration: activeIntegration?.platformName ?? '',
          },
        )}
        actionButtonOnClick={dispatchSyncTransactions}
        actionButtonLoading={postPushingTransactions}
        actionButtonVariant="primary"
        topLevelError={postPushingTransactionsError?.error}
        isCancelButtonLink
        underline={false}
        cancelButtonOnClick={closePopup}
        cancelButtonLabel={
          <span
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.cta,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            {translate('Cancel')}
          </span>
        }
      />
    </Popup>
  )
}

export default SyncTransactionsPopup
